// React
import React from 'react';
// External
import { withRouter } from 'react-router-dom';
import {
    Col, Container, Form, Row
} from 'react-bootstrap';
import { connect } from 'react-redux';
import { Layer, Stage, Text } from 'react-konva';
// Internal
import Selector from 'components/shared/atoms/Selector/Selector';
import EngravingSelector from 'components/shared/atoms/EngravingSelector/EngravingSelector';
import { updateEngraving } from 'stores/actions';
// Style
import './styles.scss';
// Constants
import FONT_FACE from 'constants/fontFaceOptions';
import FONT_SIZE from 'constants/fontSizeOptions';

/**
 * Component with a form to customise the text of the engraving
 * @param {Object} product The product associated with the engraving
 */
class CustomiseText extends React.Component {
    constructor(props) {
        super(props);
        this.petsNameFontSize = 'petsNameFontSize';
        this.additionFontSizeOne = 'additionFontSizeOne';
        this.additionFontSizeTwo = 'additionFontSizeTwo';
        this.pxToMm = 0.2645833333; // 1 px  = 0.2645833333 mm
        this.ptToPx = 1.333333; // 1 pt = 1.333333 px
        this.textSizeScale = 0.8; // to to make text sizes the same with Microsoft Word text sizes, we use this scale

        this.state = {
            errors: [],
            petsNameMax: 11,
            additionTextOneMax: 29,
            additionTextTwoMax: 29,
            selectedPlaqueSize: null,
            aspectW: 500,
            aspectH: 500,
            currentPetsText: 0,
            currentAdditionalTextOne: 0,
            currentAdditionalTextTwo: 0,
            petsNameHeight: 100,
            textOneHeight: 100,
            textTwoHeight: 100,
            nameAdjustment: 0,
            text1Adjustment: 0,
            text2Adjustment: 0,
            selectedConfig: null,
            petName: {
                text: '',
                size: 0,
                configSize: 100,
                scaledSize: 100,
                font: 'Arial',
                position: {
                    x: 0,
                    y: 90
                }
            },
            primaryText: {
                text: '',
                size: 0,
                configSize: 100,
                scaledSize: 100,
                font: 'Arial',
                position: {
                    x: 0,
                    y: 125
                }
            },
            secondaryText: {
                text: '',
                size: 0,
                configSize: 100,
                scaledSize: 100,
                font: 'Arial',
                position: {
                    x: 0,
                    y: 150
                }
            }
        };

        this.hasError = this.hasError.bind(this);
        this.calculateCanvasSize = this.calculateCanvasSize.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    componentDidMount() {
        this.initialiseStates();
    }

    handlePetsName(e) {
        const input = e.target.value;
        this.setState((prevState) => ({
            currentPetsText: input.length,
            petName: {
                ...prevState.petName,
                text: input
            }
        }));
    }

    handleTextOne(e) {
        const input = e.target.value;
        this.setState((prevState) => ({
            currentAdditionalTextOne: input.length,
            primaryText: {
                ...prevState.primaryText,
                text: input
            }
        }));
    }

    handleTextTwo(e) {
        const input = e.target.value;
        this.setState((prevState) => ({
            currentAdditionalTextTwo: input.length,
            secondaryText: {
                ...prevState.secondaryText,
                text: input
            }
        }));
    }

    async handleSubmit() {
        const errors = [];

        if (this.state.petName.text === '') {
            errors.push('petsName');
        }

        if (this.state.petName.font === '') {
            errors.push('petsNameFontFace');
        }

        if (this.state.petName.size === '') {
            errors.push('petsNameFontSize');
        }

        if (this.state.primaryText.text === '') {
            errors.push('additionalTextOne');
        }

        if (this.state.primaryText.font === '') {
            errors.push('additionFontFaceOne');
        }

        if (this.state.primaryText.size === '') {
            errors.push('additionFontSizeOne');
        }

        if (this.state.secondaryText.text === '') {
            errors.push('additionalTextTwo');
        }

        if (this.state.secondaryText.font === '') {
            errors.push('additionFontFaceTwo');
        }

        if (this.state.secondaryText.size === '') {
            errors.push('additionFontSizeTwo');
        }

        await this.setError(errors);

        const container = document.querySelector('.konvajs-content canvas');
        const img = container.toDataURL();

        const engraving = {
            ...this.props.engraving,
            customTextImage: img,
            petName: {
                ...this.state.petName,
                max: parseInt(this.state.selectedConfig.Name_Max, 10),
                size: this.state.petName.scaledSize
            },
            primaryText: {
                ...this.state.primaryText,
                max: parseInt(this.state.selectedConfig.Text1_Max, 10),
                size: this.state.primaryText.scaledSize
            },
            secondaryText: {
                ...this.state.secondaryText,
                max: parseInt(this.state.selectedConfig.Text2_Max, 10),
                size: this.state.secondaryText.scaledSize
            },
            id: this.state.selectedConfig.ID,
            product: this.state.selectedConfig.Product,
            size: this.state.selectedConfig.Size,
            ratios: this.state.selectedConfig.Ratios,
            shape: this.state.selectedConfig.Shape,
            color: this.state.selectedConfig.Color,
            dimensions: this.state.selectedConfig.Dimensions,
            iconRatio: this.state.selectedConfig.Icon_Ratio,
            nameSize: this.state.selectedConfig.Name_Size,
            photoSize: this.state.selectedConfig.PhotoSize
        };

        return errors.length > 0 ? null : engraving;
    }

    onFontSizeChanged(value, caller) {
        const {
            aspectW,
            aspectH
        } = this.state;

        if (caller === this.petsNameFontSize) {
            const { text1Adjustment, text2Adjustment } = this.state;
            const nameAdjustment = value - this.state.petName.configSize;
            const returnValue = {
                petName: {
                    ...this.state.petName,
                    scaledSize: value
                },
                nameAdjustment
            };

            this.calculateCanvasSize(
                returnValue,
                aspectW,
                aspectH,
                this.state.petName.configSize,
                nameAdjustment,
                this.state.primaryText.configSize,
                text1Adjustment,
                this.state.secondaryText.configSize,
                text2Adjustment
            );
        } else if (caller === this.additionFontSizeOne) {
            const { nameAdjustment, text2Adjustment } = this.state;
            const text1Adjustment = value - this.state.primaryText.configSize;
            const returnValue = {
                primaryText: {
                    ...this.state.primaryText,
                    scaledSize: value
                },
                text1Adjustment
            };

            this.calculateCanvasSize(
                returnValue,
                aspectW,
                aspectH,
                this.state.petName.configSize,
                nameAdjustment,
                this.state.primaryText.configSize,
                text1Adjustment,
                this.state.secondaryText.configSize,
                text2Adjustment
            );
        } else { // if (caller === this.additionFontSizeTwo)
            const { nameAdjustment, text1Adjustment } = this.state;
            const text2Adjustment = value - this.state.secondaryText.configSize;
            const returnValue = {
                secondaryText: {
                    ...this.state.secondaryText,
                    scaledSize: value
                },
                text2Adjustment
            };

            this.calculateCanvasSize(
                returnValue,
                aspectW,
                aspectH,
                this.state.petName.configSize,
                nameAdjustment,
                this.state.primaryText.configSize,
                text1Adjustment,
                this.state.secondaryText.configSize,
                text2Adjustment
            );
        }
    }

    setError(error) {
        this.setState({ errors: error });
    }

    initialiseStates() {
        const selectedVessel = this.props.engraving.productId;
        fetch('../fpc_config.json')
            .then((r) => r.json())
            .then((data) => {
                const selectedConfig = data.Config_File.find((x) => x.SugarID === selectedVessel);
                let selectedPlaqueWidth;
                let selectedPlaqueHeight;

                if (selectedConfig.Dimensions.indexOf('x') > -1) {
                    const ratioArr = selectedConfig.Dimensions.split('x');
                    selectedPlaqueWidth = ratioArr[0];
                    selectedPlaqueHeight = ratioArr[1];
                } else {
                    const ratio = selectedConfig.Dimensions;
                    selectedPlaqueWidth = ratio;
                    selectedPlaqueHeight = ratio;
                }

                let petName = this.props.engraving.petName.text
                    ? this.props.engraving.petName.text
                    : this.props.booking.pet.getFullName();

                // only remove the owner last name if this is the first time the engraving created
                if (
                    !this.props.engraving.petName.text
                    && this.props.booking.owner !== null
                    && petName.includes(this.props.booking.owner.lastName)
                ) {
                    const petNameArr = petName.split(' ');
                    if (
                        petNameArr.length > 1
                        && petNameArr[petNameArr.length - 1] === this.props.booking.owner.lastName
                    ) {
                        petNameArr.pop();
                        petName = petNameArr.join(' ');
                    }
                }

                this.setState((prevState) => ({
                    selectedConfig,
                    selectedPlaqueSize: selectedConfig.Dimensions,
                    petName: {
                        ...prevState.petName,
                        text: petName,
                        size: this.props.engraving.petName.size,
                        configSize: selectedConfig.Name_Size,
                        scaledSize: this.props.engraving.petName.size
                            ? this.props.engraving.petName.size
                            : selectedConfig.Name_Size,
                        font: this.props.engraving.petName.font
                    },
                    primaryText: {
                        ...prevState.primaryText,
                        text: this.props.engraving.primaryText.text,
                        size: this.props.engraving.primaryText.size,
                        configSize: selectedConfig.Text1_Size,
                        scaledSize: this.props.engraving.primaryText.size
                            ? this.props.engraving.primaryText.size
                            : selectedConfig.Text1_Size,
                        font: this.props.engraving.primaryText.font
                    },
                    secondaryText: {
                        ...prevState.secondaryText,
                        text: this.props.engraving.secondaryText.text,
                        size: this.props.engraving.secondaryText.size,
                        configSize: selectedConfig.Text2_Size,
                        scaledSize: this.props.engraving.secondaryText.size
                            ? this.props.engraving.secondaryText.size
                            : selectedConfig.Text2_Size,
                        font: this.props.engraving.secondaryText.font
                    }
                }), () => {
                    if (this.state.selectedPlaqueSize === '0') {
                        alert('Sorry, the selected plaque size doesn\'t '
                            + 'support image options');
                    } else {
                        this.setState(() => {
                            return {
                                aspectW: selectedPlaqueWidth,
                                aspectH: selectedPlaqueHeight
                            };
                        }, () => {
                            this.onFontSizeChanged(this.state.petName.scaledSize, this.petsNameFontSize);
                            this.onFontSizeChanged(this.state.primaryText.scaledSize, this.additionFontSizeOne);
                            this.onFontSizeChanged(this.state.secondaryText.scaledSize, this.additionFontSizeTwo);
                        });
                    }
                });
            });
    }

    hasError(fieldError) {
        return this.state.errors.indexOf(fieldError) >= 0;
    }

    calculateCanvasSize(
        returnValue,
        selectedPlaqueWidth,
        selectedPlaqueHeight,
        petsNameConfigSize,
        nameAdjustment,
        textOneConfigSize,
        text1Adjustment,
        textTwoConfigSize,
        text2Adjustment
    ) {
        this.setState(() => returnValue, () => {
            const originalWidth = 500; // just for info, originalHeight is also 500
            const adjustedWidth = originalWidth;
            const adjustedHeight = (selectedPlaqueHeight / selectedPlaqueWidth) * originalWidth;
            const scale = selectedPlaqueWidth / (adjustedWidth * this.pxToMm);
            const adjustedPetsNameSize = (((parseInt(petsNameConfigSize, 10)
                + parseInt(nameAdjustment, 10)) * this.ptToPx) / scale) * this.textSizeScale;
            const adjustedTextOneSize = (((parseInt(textOneConfigSize, 10)
                + parseInt(text1Adjustment, 10)) * this.ptToPx) / scale) * this.textSizeScale;
            const adjustedTextTwoSize = (((parseInt(textTwoConfigSize, 10)
                + parseInt(text2Adjustment, 10)) * this.ptToPx) / scale) * this.textSizeScale;
            const totalAdjustedTextSize = adjustedPetsNameSize + adjustedTextOneSize + adjustedTextTwoSize + 20;
            this.setState((prevState) => ({
                adjustedWidth,
                adjustedHeight,
                petName: {
                    ...prevState.petName,
                    size: adjustedPetsNameSize,
                    position: {
                        x: 0,
                        y: adjustedHeight / 2 - (totalAdjustedTextSize) / 2
                    }
                },
                primaryText: {
                    ...prevState.primaryText,
                    size: adjustedTextOneSize,
                    position: {
                        x: 0,
                        y: adjustedHeight / 2 - (totalAdjustedTextSize) / 2 + adjustedPetsNameSize + 10
                    }
                },
                secondaryText: {
                    ...prevState.secondaryText,
                    size: adjustedTextTwoSize,
                    position: {
                        x: 0,
                        y: adjustedHeight / 2 - (totalAdjustedTextSize) / 2 + adjustedPetsNameSize + 10
                            + adjustedTextOneSize + 10
                    }
                }
            }));
        });
    }

    render() {
        return (
            <Container fluid className="engraving-container customise-text-container">
                <Row>
                    <Col md={7} style={{ paddingLeft: '0' }}>
                        <div className="section-description">
                            <p>
                                Choose message text, as well as font and size.
                                Engraving includes your pet’s name, plus two additional lines of text as
                                submitted.
                            </p>
                        </div>

                        <div className="engraving-options-wrapper">
                            <Form className="engraving-options-form">
                                <Form.Row>
                                    <Form.Group as={Col} controlId="petsName" className="control-group">
                                        <Form.Label>Pet&apos;s Name</Form.Label>
                                        <div className="max-char">
                                            <p>
                                                {`Chars ${this.state.currentPetsText}`}
                                                {`/${this.state.petsNameMax}`}
                                            </p>
                                        </div>
                                        <Form.Control
                                            name="petsName"
                                            type="text"
                                            placeholder="Pet's Name"
                                            onChange={(e) => this.handlePetsName(e)}
                                            className={this.hasError('petsName') ? 'is-invalid' : ''}
                                            haserror={this.hasError('petsName') ? 1 : 0}
                                            maxLength={this.state.petsNameMax}
                                            value={this.state.petName.text}
                                        />
                                    </Form.Group>
                                    <Form.Group
                                        as={Col}
                                        controlId="petsFontSize"
                                        className="control-group"
                                    >
                                        <Form.Label>Font Size</Form.Label>
                                        <Selector
                                            options={FONT_SIZE}
                                            placeholder="Font Size"
                                            name="petsNameFontSize"
                                            controlClassName={this.hasError('petsNameFontSize') ? 'is-invalid' : ''}
                                            hasError={this.hasError('petsNameFontSize')}
                                            onChange={(value) => {
                                                this.onFontSizeChanged(value, this.petsNameFontSize);
                                            }}
                                            value={parseInt(this.state.petName.scaledSize, 10)}
                                        />
                                    </Form.Group>
                                    <Form.Group
                                        as={Col}
                                        controlId="petsFontFace"
                                        className="control-group"
                                    >
                                        <Form.Label>Font Face</Form.Label>
                                        <EngravingSelector
                                            options={FONT_FACE}
                                            placeholder="Font Face"
                                            name="petsNameFontFace"
                                            controlClassName={this.hasError('petsNameFontFace')
                                                ? 'is-invalid' : ''}
                                            hasError={this.hasError('petsNameFontFace')}
                                            value={this.state.petName.font}
                                            onChange={(value) => {
                                                // Updates the state
                                                this.setState((prevState) => ({
                                                    petName: {
                                                        ...prevState.petName,
                                                        font: value
                                                    }
                                                }));
                                            }}
                                        />
                                    </Form.Group>
                                </Form.Row>

                                <Form.Row>
                                    <Form.Group
                                        as={Col}
                                        controlId="additionalTextOne"
                                        className="control-group"
                                    >
                                        <Form.Label style={{ marginTop: '-12px' }}>
                                            Text Line
                                            1
                                        </Form.Label>
                                        <div className="max-char">
                                            <p>
                                                {`Chars ${this.state.currentAdditionalTextOne}`}
                                                {`/${this.state.additionTextOneMax}`}
                                            </p>
                                        </div>
                                        <Form.Control
                                            name="additionalTextOne"
                                            type="text"
                                            placeholder="Text Line 1"
                                            onChange={(e) => this.handleTextOne(e)}
                                            className={
                                                this.hasError('additionalTextOne') ? 'is-invalid' : ''
                                            }
                                            haserror={this.hasError('additionalTextOne') ? 1 : 0}
                                            maxLength={this.state.additionTextOneMax}
                                            value={this.state.primaryText.text}
                                        />
                                    </Form.Group>
                                    <Form.Group
                                        as={Col}
                                        controlId="additionFontSizeOne"
                                        className="control-group"
                                    >
                                        <Form.Label>Font Size</Form.Label>
                                        <Selector
                                            options={FONT_SIZE}
                                            placeholder="Font Size"
                                            name="additionFontSizeOne"
                                            controlClassName={
                                                this.hasError('additionFontSizeOne')
                                                    ? 'is-invalid' : ''
                                            }
                                            hasError={this.hasError('additionFontSizeOne')}
                                            onChange={(value) => {
                                                this.onFontSizeChanged(value, this.additionFontSizeOne);
                                            }}
                                            value={parseInt(this.state.primaryText.scaledSize, 10)}
                                        />
                                    </Form.Group>
                                    <Form.Group
                                        as={Col}
                                        controlId="additionFontFaceOne"
                                        className="control-group"
                                    >
                                        <Form.Label>Font Face</Form.Label>
                                        <EngravingSelector
                                            options={FONT_FACE}
                                            placeholder="Font Face"
                                            name="additionFontFaceOne"
                                            hasError={this.hasError('additionFontFaceOne')}
                                            controlClassName={
                                                this.hasError('additionFontFaceOne')
                                                    ? 'is-invalid' : ''
                                            }
                                            value={this.state.primaryText.font}
                                            onChange={(value) => {
                                                // Updates the state
                                                this.setState((prevState) => ({
                                                    primaryText: {
                                                        ...prevState.primaryText,
                                                        font: value
                                                    }
                                                }));
                                            }}
                                        />
                                    </Form.Group>
                                </Form.Row>

                                <Form.Row>
                                    <Form.Group
                                        as={Col}
                                        controlId="additionalTextTwo"
                                        className="control-group"
                                    >
                                        <Form.Label style={{ marginTop: '-12px' }}>
                                            Text Line
                                            2
                                        </Form.Label>
                                        <div className="max-char">
                                            <p>
                                                {`Chars ${this.state.currentAdditionalTextTwo}`}
                                                {`/${this.state.additionTextTwoMax}`}
                                            </p>
                                        </div>
                                        <Form.Control
                                            name="additionalTextTwo"
                                            type="text"
                                            placeholder="Text Line 2"
                                            onChange={(e) => this.handleTextTwo(e)}
                                            className={
                                                this.hasError('additionalTextTwo') ? 'is-invalid' : ''
                                            }
                                            haserror={this.hasError('additionalTextTwo') ? 1 : 0}
                                            maxLength={this.state.additionTextTwoMax}
                                            value={this.state.secondaryText.text}
                                        />
                                    </Form.Group>
                                    <Form.Group
                                        as={Col}
                                        controlId="additionFontSizeTwo"
                                        className="control-group"
                                    >
                                        <Form.Label>Font Size</Form.Label>
                                        <Selector
                                            options={FONT_SIZE}
                                            placeholder="Font Size"
                                            name="additionFontSizeTwo"
                                            controlClassName={
                                                this.hasError('additionFontSizeTwo') ? 'is-invalid' : ''
                                            }
                                            hasError={this.hasError('additionFontSizeTwo') ? 1 : 0}
                                            onChange={(value) => {
                                                this.onFontSizeChanged(value, this.additionFontSizeTwo);
                                            }}
                                            value={parseInt(this.state.secondaryText.scaledSize, 10)}
                                        />
                                    </Form.Group>
                                    <Form.Group
                                        as={Col}
                                        controlId="additionFontFaceTwo"
                                        className="control-group"
                                    >
                                        <Form.Label>Font Face</Form.Label>
                                        <EngravingSelector
                                            options={FONT_FACE}
                                            placeholder="Font Face"
                                            name="additionFontFaceTwo"
                                            controlClassName={
                                                this.hasError('additionFontFaceTwo')
                                                    ? 'is-invalid' : ''
                                            }
                                            hasError={this.hasError('additionFontFaceTwo') ? 1 : 0}
                                            value={this.state.secondaryText.font}
                                            onChange={(value) => {
                                                // Updates the state
                                                this.setState((prevState) => ({
                                                    secondaryText: {
                                                        ...prevState.secondaryText,
                                                        font: value
                                                    }
                                                }));
                                            }}
                                        />
                                    </Form.Group>
                                </Form.Row>

                            </Form>
                        </div>
                    </Col>
                    <Col md={5}>
                        <Stage
                            width={this.state.adjustedWidth}
                            height={this.state.adjustedHeight}
                            verticalAlign="middle"
                            className="engraving-canvas-customise-text options customise"
                        >

                            <Layer>
                                <Text
                                    fontSize={parseFloat(this.state.petName.size)}
                                    fontFamily={this.state.petName.font}
                                    text={this.state.petName.text}
                                    align="center"
                                    x={this.state.petName.position.x}
                                    y={this.state.petName.position.y}
                                    width={this.state.adjustedWidth}
                                    height={this.state.petsNameHeight}
                                />
                                <Text
                                    fontSize={parseFloat(this.state.primaryText.size)}
                                    fontFamily={this.state.primaryText.font}
                                    text={this.state.primaryText.text}
                                    align="center"
                                    x={this.state.primaryText.position.x}
                                    y={this.state.primaryText.position.y}
                                    width={this.state.adjustedWidth}
                                    height={this.state.textOneHeight}
                                />
                                <Text
                                    fontSize={parseFloat(this.state.secondaryText.size)}
                                    fontFamily={this.state.secondaryText.font}
                                    text={this.state.secondaryText.text}
                                    align="center"
                                    x={this.state.secondaryText.position.x}
                                    y={this.state.secondaryText.position.y}
                                    width={this.state.adjustedWidth}
                                    height={this.state.textTwoHeight}
                                />
                            </Layer>
                        </Stage>
                    </Col>
                </Row>
            </Container>
        );
    }
}

const mapStateToProps = (state) => ({
    booking: state.booking,
    engraving: state.engraving
});

const mapDispatchToProps = (dispatch) => {
    return {
        updateEngraving: (engravingObj) => dispatch(updateEngraving(engravingObj))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CustomiseText));
